import { isTimedRefusal, isTransportFailure, toRefusal } from './refusal';
import type { Refusal, TimedRefusalCode } from './refusal';

export type CodeProblem =
  | { readonly kind: 'transport' }
  | { readonly kind: 'refused'; readonly refusal: Refusal }
  | { readonly kind: 'switchNotSaved' }
  | { readonly kind: 'switchUnconfirmed' };

export type BlockReason = Exclude<TimedRefusalCode, 'RESEND_TOO_SOON'>;

export type CodeStatus =
  | { readonly kind: 'entering' }
  | { readonly kind: 'verifying' }
  | { readonly kind: 'refused'; readonly problem: CodeProblem }
  | { readonly kind: 'expired' }
  | { readonly kind: 'blocked'; readonly reason: BlockReason; readonly seconds: number };

export interface LiveHold {
  readonly reason: TimedRefusalCode;
  readonly seconds: number;
}

export interface CodeFacts {
  readonly verifying: boolean;
  readonly problem: CodeProblem | null;
  readonly hold: LiveHold | null;
  readonly expired: boolean;
}

export const codeProblemOf = (error: unknown): CodeProblem | null => {
  if (error === null || error === undefined) return null;
  return isTransportFailure(error) ? { kind: 'transport' } : { kind: 'refused', refusal: toRefusal(error) };
};

export const untimedProblem = (problem: CodeProblem | null): CodeProblem | null =>
  problem?.kind === 'refused' && isTimedRefusal(problem.refusal.code) ? null : problem;

const SPENT_CODE_REFUSALS: readonly string[] = ['CODE_EXPIRED', 'LOCKED'];

const spentBy = (problem: CodeProblem | null): boolean =>
  problem?.kind === 'refused' && problem.refusal.code !== null && SPENT_CODE_REFUSALS.includes(problem.refusal.code);

const blockOf = (hold: LiveHold | null): CodeStatus | null => {
  if (hold === null || hold.reason === 'RESEND_TOO_SOON') return null;
  return { kind: 'blocked', reason: hold.reason, seconds: hold.seconds };
};

export const codeStatusOf = ({ verifying, problem, hold, expired }: CodeFacts): CodeStatus => {
  if (verifying) return { kind: 'verifying' };
  const blocked = blockOf(hold);
  if (blocked !== null) return blocked;
  if (expired || spentBy(problem)) return { kind: 'expired' };
  return problem === null ? { kind: 'entering' } : { kind: 'refused', problem };
};
